import React, { useContext, useMemo, useState } from 'react';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { ProductMasterDataContext } from '../../../../../context/ProductMasterDataContext';
import { AddButton } from '../../../../components/Button';
import { ProductMasterData } from '../../../../../types/product_master_data/productMasterData';
import Table from '../../../../components/Table';
import { defaultKitTemplateTable, KitTemplateTableSettings } from '../../../../../types/kit_template/kitTemplate.table';
import { KitTemplateContext } from '../../../../../context/KitTemplateContext';
import { KitTemplateTable } from '../../../Common/KitTemplateTable';
import { CompanyContext } from '../../../../../context/CompanyContext';
import SearchBarWithFilter from '../../../../../VentoryUI/components/common/SearchBarWithFilter';
import { KitTemplateFilter } from '../../../../../VentoryUI/components/filters/KitFilter/KitTemplateFilter';
import { CustomFieldContext } from '../../../../../context/CustomFieldContext';
import { TagRelationContext } from '../../../../../context/TagRelationContext';
import CreateKitTemplateModal from '../Modals/CreateKitTemplateModal';
import UpdateKitTemplateModal from '../Modals/UpdateKitTemplateModal';
import DeleteKitTemplateModal from '../Modals/DeleteKitTemplateModal';
import { useMutation } from '@apollo/client';
import {
  DeleteKitTemplateResponse,
  DeleteKitTemplateVariables,
  KitTemplateMutations,
} from '../../../../../graphql/kitTemplate.graphql';
import { ToastType } from '../../../../components/Toast';
import { useToast } from '../../../../../providers/ToastProvider';
import { KitTemplateInput } from '../../../../../types/kit_template/kitTemplate.input';
import { KitTemplate } from '../../../../../types/kit_template/kitTemplate';

interface ProductMasterDataKitPaneProps {
  pmdId: string;
  footer: (input: ProductMasterData) => React.JSX.Element;
}

export default function ProductMasterDataKitPane({ pmdId, footer }: ProductMasterDataKitPaneProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { kitTemplatesLoading, kitTemplates, setKitTemplates } = useContext(KitTemplateContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { customFields } = useContext(CustomFieldContext);
  const { showToast } = useToast();

  const pmd = productMasterData.get(pmdId);
  if (!pmd) return null;

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selected, setSelected] = useState<string | undefined>(undefined);

  const allItems = useMemo(() => {
    return [...kitTemplates.values()].filter(kt => kt.productMasterDataId === pmdId);
  }, [productMasterData, kitTemplates]);

  const [items, setItems] = useState(allItems);

  const filter = new KitTemplateFilter(productMasterData, tagRelations, customFields, currentCompany);

  const [remove, { loading }] = useMutation<DeleteKitTemplateResponse, DeleteKitTemplateVariables>(
    KitTemplateMutations.remove,
    {
      onCompleted: response => {
        kitTemplates.delete(response.deleteKitTemplate[0].id);
        setKitTemplates(new Map(kitTemplates));
        showToast({ type: ToastType.success, title: t(TranslationKey.kitTemplateDeletedSuccessfully).singular.upper });
        setDeleteModalOpen(false);
        setSelected(undefined);
      },
    },
  );

  const handleDelete = async () => {
    if (!selected) return;

    const template = kitTemplates.get(selected);
    if (!template) return;

    await remove({ variables: { templates: [new KitTemplateInput(template).forDelete()] } });
  };

  return (
    <>
      {createModalOpen && <CreateKitTemplateModal open={createModalOpen} setOpen={setCreateModalOpen} pmd={pmd} />}
      {deleteModalOpen && selected && (
        <DeleteKitTemplateModal
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          remove={handleDelete}
          loading={loading}
        />
      )}
      {updateModalOpen && selected && (
        <UpdateKitTemplateModal open={updateModalOpen} setOpen={setUpdateModalOpen} id={selected} />
      )}

      <div className={`${pmdId ? '' : 'content-x'} flex h-full flex-grow flex-col gap-4`}>
        <div className='grid gap-2'>
          <p className='page-title'>{t(TranslationKey.kitScreenTitle).singular.upper}</p>
          <p className='subtitle'>{t(TranslationKey.kitScreenSubtitle).singular.upper}</p>
        </div>
        <div className='flex justify-between gap-4'>
          <SearchBarWithFilter
            loading={productMasterDataLoading}
            items={allItems}
            setItems={setItems}
            placeholder={t(TranslationKey.kitTemplateFilter).singular.label}
            filter={filter}
          />
          <div className='flex'>
            <AddButton
              text={t(TranslationKey.kitTemplateAdd).singular.label}
              onClick={() => setCreateModalOpen(true)}
            />
          </div>
        </div>
        <Table
          loading={kitTemplatesLoading || productMasterDataLoading}
          items={items}
          headers={KitTemplateTable.header(
            productMasterData,
            new KitTemplateTableSettings({ companyId: currentCompany.id, columns: defaultKitTemplateTable }),
            (item: KitTemplate) => {
              setDeleteModalOpen(true);
              setSelected(item.id);
            },
          )}
          onClick={item => {
            setSelected(item.id);
            setUpdateModalOpen(true);
          }}
          totalItemCount={items.length}
          filter={filter}
        />
      </div>
    </>
  );
}
